import { apiClient as api } from "./httpClient";

// Evoluções do paciente: registro clínico por sessão (agendamento), com nota do objetivo do
// plano terapêutico, escala GAS e catálogos (condição, situação, abordagem, recurso).
// O autor altera/exclui só dentro da janela de edição; o conteúdo dos textos só chega a quem
// tem acesso (`canRead`) — para os demais a linha apenas informa que a evolução existe.

export interface PatientEvolution {
  id: string;
  /** nulo nas evoluções herdadas sem agendamento */
  scheduleId: string | null;
  /** ISO no relógio da entidade (sem offset): data/hora da sessão */
  sessionDate: string;
  professionalId: string | null;
  professionalName: string | null;
  professionalDocument?: string | null;
  serviceTypeName: string | null;
  createdBy: string | null;
  createdByName: string;
  createdByDocument?: string | null;
  createdAt: string;
  updatedAt: string | null;
  disregardPlan: boolean;
  planId: string | null;
  planTitle: string | null;
  planPeriodTitle: string | null;
  /** estado atual (0 a 10) declarado no objetivo */
  planCurrentScale: number | null;
  /** alvo (0 a 10) do objetivo */
  planTargetScale: number | null;
  /** 0 a 10 */
  planEvolutionScale: number | null;
  gasScale: string | null;
  patientConditionId: string | null;
  patientConditionName: string | null;
  patientSituationId: string | null;
  patientSituationName: string | null;
  patientConditionJustification: string | null;
  aproachId: string | null;
  aproachName: string | null;
  therapeuticResourceId: string | null;
  therapeuticResourceName: string | null;
  therapeuticEvolution: string | null;
  guidance: string | null;
  observation: string | null;
  /** autor = usuário da sessão */
  isMine: boolean;
  /** autor E dentro da janela de edição — o servidor recusa o resto */
  canEdit: boolean;
  /** false: os textos vêm nulos; a linha só informa que a evolução existe */
  canRead: boolean;
  /** RN-542k: evolução de sessão ou avulsa */
  kind?: EvolutionKind;
}

/** RN-542k: session = Evolução de sessão (ligada a um agendamento); standalone = Evolução avulsa. */
export type EvolutionKind = "session" | "standalone";

export const EVOLUTION_KINDS: { value: EvolutionKind; label: string; help: string }[] = [
  { value: "session", label: "Evolução de sessão", help: "Registro de um atendimento da agenda." },
  { value: "standalone", label: "Evolução avulsa", help: "Registro clínico sem sessão: contato com escola, retorno de exame, intercorrência." },
];

export const evolutionKindLabel = (kind?: EvolutionKind | null): string =>
  EVOLUTION_KINDS.find(k => k.value === (kind ?? "session"))?.label ?? "Evolução de sessão";

/** Resumo do objetivo do plano para o formulário: dados do objetivo + histórico de notas por sessão. */
export interface PlanEvaluationPoint {
  evolutionId: string;
  /** ISO no relógio da entidade (sem offset) */
  sessionDate: string;
  scale: number;
}

export interface TherapyGoalSummary {
  id: string;
  title: string;
  periodTitle: string | null;
  goal: string | null;
  metric: string | null;
  justify: string | null;
  notes: string | null;
  status: string | null;
  priority: string | null;
  currentScale: number | null;
  targetScale: number | null;
  sessionsQuantity: number | null;
  sessionsDone: number;
  /** yyyy-MM-dd */
  initialDate: string | null;
  finalDate: string | null;
  isConcluded: boolean;
  isExpired: boolean;
  history: PlanEvaluationPoint[];
}

export interface PatientEvolutionAuthor {
  id: string;
  name: string;
}

export interface PatientEvolutionsListResult {
  items: PatientEvolution[];
  totalCount: number;
  /** quem já evoluiu este paciente (independe dos filtros) */
  authors: PatientEvolutionAuthor[];
  /** dias da janela de edição (configuração do produto) */
  editWindowDays: number;
}

export interface PatientEvolutionsQueryParams {
  search?: string;
  createdBy?: string;
  from?: string;
  to?: string;
  /** vazio = todas */
  kind?: EvolutionKind;
  pageNumber: number;
  pageSize: number;
}

export interface EvolutionScheduleOption {
  id: string;
  /** ISO no relógio da entidade (sem offset) */
  dateTime: string;
  professionalId: string | null;
  professionalName: string | null;
  professionalDocument?: string | null;
  serviceTypeName: string | null;
  hasEvolution: boolean;
  /** Presença com significado "atendido" (RN-114d) — filtro "Realizadas" do formulário */
  attended: boolean;
  /** Sessão cancelada cujo status permite evolução: aparece em "Todas" e não recebe presença */
  cancelled: boolean;
  /** Presença em branco e sessão não encerrada: o formulário oferece "Atendimento realizado" (RN-542j) */
  canMarkAttended: boolean;
  /** Status atuais do agendamento */
  confirmation: string | null;
  confirmationColor: string | null;
  presence: string | null;
  presenceColor: string | null;
}

export interface EvolutionPlanOption {
  id: string;
  title: string;
  periodTitle: string | null;
  currentScale: number | null;
  targetScale: number | null;
  status: string | null;
}

export interface CatalogOption {
  id: string;
  name: string;
  /** item "só para mim" do usuário logado (abordagem/recurso cadastrado por ele) */
  onlyForMe?: boolean;
}

/** Resultado do cadastro rápido: `existing` = já havia um item visível com esse nome, nada foi criado. */
export interface QuickCreatedCatalogItem {
  id: string;
  name: string;
  existing: boolean;
  onlyForMe: boolean;
}

export const CATALOG_NAME_MIN_LENGTH = 2;
export const CATALOG_NAME_MAX_LENGTH = 100;

export interface PatientEvolutionOptions {
  schedules: EvolutionScheduleOption[];
  plans: EvolutionPlanOption[];
  conditions: CatalogOption[];
  situations: CatalogOption[];
  aproaches: CatalogOption[];
  resources: CatalogOption[];
  /** tipos de recurso, para o cadastro rápido de recurso */
  resourceTypes: CatalogOption[];
  gasScales: string[];
  scheduleWindowDays: number;
  editWindowDays: number;
  patientName: string;
}

export interface PatientEvolutionPayload {
  /** nulo na evolução avulsa */
  scheduleId: string | null;
  disregardPlan: boolean;
  planId: string | null;
  planEvolutionScale: number | null;
  gasScale: string | null;
  patientConditionId: string | null;
  patientSituationId: string | null;
  patientConditionJustification: string | null;
  aproachId: string | null;
  therapeuticResourceId: string | null;
  therapeuticEvolution: string;
  guidance: string | null;
  observation: string | null;
  /** Check "Atendimento realizado" — só em registro novo; marca a presença do agendamento (RN-542j) */
  markAttended?: boolean;
  /** RN-542k: tipo da evolução (padrão session) */
  kind?: EvolutionKind;
  /** Evolução avulsa: data do registro (yyyy-MM-dd), hoje ou antes */
  recordDate?: string | null;
}

export const EVOLUTION_TEXT_MAX_LENGTH = 8000;
export const EVOLUTION_GUIDANCE_MAX_LENGTH = 2000;
export const EVOLUTION_OBSERVATION_MAX_LENGTH = 2000;
export const EVOLUTION_JUSTIFICATION_MAX_LENGTH = 1000;
export const EVOLUTION_SCALE_MIN = 0;
export const EVOLUTION_SCALE_MAX = 10;

/** Timeout largo de propósito: o servidor grava no sistema legado antes de responder. */
const WRITE_TIMEOUT = 60000;

export const patientEvolutionsService = {
  getByPatient: async (patientId: string, params: PatientEvolutionsQueryParams): Promise<PatientEvolutionsListResult> => {
    const response = await api.get(`/patient-evolutions/by-patient/${patientId}`, { params });
    return response.data;
  },

  /** `scheduleId`/`planId` (edição) trazem o agendamento/objetivo já gravados mesmo fora da janela. */
  getOptions: async (
    patientId: string,
    current?: { scheduleId?: string | null; planId?: string | null },
  ): Promise<PatientEvolutionOptions> => {
    const response = await api.get(`/patient-evolutions/by-patient/${patientId}/options`, {
      params: {
        scheduleId: current?.scheduleId || undefined,
        planId: current?.planId || undefined,
      },
    });
    return response.data;
  },

  /** Dados do objetivo do plano e histórico de notas, para o formulário de evolução. */
  getPlanSummary: async (patientId: string, planId: string): Promise<TherapyGoalSummary> => {
    const response = await api.get(`/patient-evolutions/by-patient/${patientId}/plans/${planId}`);
    return response.data;
  },

  create: async (patientId: string, payload: PatientEvolutionPayload): Promise<string> => {
    const response = await api.post("/patient-evolutions", { patientId, ...payload }, { timeout: WRITE_TIMEOUT });
    return response.data;
  },

  update: async (id: string, payload: PatientEvolutionPayload): Promise<void> => {
    await api.put(`/patient-evolutions/${id}`, payload, { timeout: WRITE_TIMEOUT });
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/patient-evolutions/${id}`, { timeout: WRITE_TIMEOUT });
  },

  quickCreateApproach: async (name: string, onlyForMe: boolean): Promise<QuickCreatedCatalogItem> => {
    const response = await api.post("/patient-evolutions/approaches", { name, onlyForMe });
    return response.data;
  },

  quickCreateResource: async (name: string, resourceTypeId: string, onlyForMe: boolean): Promise<QuickCreatedCatalogItem> => {
    const response = await api.post("/patient-evolutions/resources", { name, resourceTypeId, onlyForMe });
    return response.data;
  },
};

/** Erros de negócio conhecidos em PT-BR; o code vem do Result.Error do backend. */
export const mapPatientEvolutionError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? "";
  const messages: Record<string, string> = {
    "PatientEvolution.NotFound": "Evolução não encontrada (pode ter sido excluída).",
    "PatientEvolution.PatientNotFound": "Paciente não encontrado na entidade.",
    "PatientEvolution.EmptyEvolution": "Escreva a evolução terapêutica.",
    "PatientEvolution.TooLong": `O texto excede o limite permitido (evolução ${EVOLUTION_TEXT_MAX_LENGTH}, orientação e observações ${EVOLUTION_GUIDANCE_MAX_LENGTH}, justificativa ${EVOLUTION_JUSTIFICATION_MAX_LENGTH} caracteres).`,
    "PatientEvolution.ScheduleRequired": "Selecione o agendamento da sessão.",
    "PatientEvolution.PlanClosed": "Este objetivo está concluído ou vencido e não recebe evolução nova.",
    "PatientEvolution.NoRelation": "As evoluções ficam disponíveis apenas para o profissional que atende ou atendeu este paciente, ou que supervisiona o caso.",
    "PatientEvolution.ScheduleNotFound": "Agendamento não encontrado para este paciente.",
    "PatientEvolution.ScheduleOutOfWindow": "O agendamento está fora da janela permitida para registro de evolução.",
    "PatientEvolution.ScheduleNotMine": "Somente o profissional do agendamento pode registrar a evolução da sessão.",
    "PatientEvolution.ScheduleStatusNotEvolvable": "O status deste agendamento não permite evolução (ver Configurações da agenda).",
    "PatientEvolution.PlanRequired": "Selecione o objetivo do plano terapêutico ou marque a opção de desconsiderar o plano.",
    "PatientEvolution.PlanNotFound": "Objetivo do plano terapêutico não encontrado.",
    "PatientEvolution.ScaleRequired": "Informe a nota da sessão para o objetivo selecionado.",
    "PatientEvolution.ScaleOutOfRange": `A nota da sessão deve estar entre ${EVOLUTION_SCALE_MIN} e ${EVOLUTION_SCALE_MAX}.`,
    "PatientEvolution.InvalidGasScale": "Escala GAS inválida.",
    "PatientEvolution.CatalogNotFound": "Um dos valores selecionados (condição, situação, abordagem ou recurso) não foi encontrado.",
    "PatientEvolution.NotAuthor": "Somente quem registrou a evolução pode alterá-la ou excluí-la.",
    "PatientEvolution.EditWindowExpired": "Prazo de edição encerrado: a evolução só pode ser alterada ou excluída dentro da janela de edição após o registro.",
    "PatientEvolution.BubbleWriteFailed": "Não foi possível gravar a evolução agora. Tente novamente em instantes.",
    "PatientEvolution.Unauthorized": "Você não tem permissão para esta operação.",
    "PatientEvolution.CatalogNameInvalid": `O nome deve ter entre ${CATALOG_NAME_MIN_LENGTH} e ${CATALOG_NAME_MAX_LENGTH} caracteres.`,
    "PatientEvolution.CatalogVisibilityRequired": "Escolha para quem o item fica visível.",
    "PatientEvolution.ResourceTypeNotFound": "Tipo de recurso não encontrado.",
    "PatientEvolution.RecordDateRequired": "Informe a data do registro.",
    "PatientEvolution.RecordDateInFuture": "A data do registro não pode ser no futuro.",
    "PatientEvolution.KindChangeNotAllowed": "O tipo da evolução não pode ser alterado depois de registrado.",
    "PatientEvolution.InvalidKind": "Tipo de evolução inválido.",
  };
  return messages[code] || "Não foi possível concluir a operação. Tente novamente.";
};
